import React, { HTMLAttributes } from 'react';

export interface RectangleBoundaries {
  height: number;
  width: number;
  offsetY: number;
  offsetX: number;
  cornerRadius: number;
  borderWidth: number;
  borderStyle: string;
  borderColor: string;
  backgroundStyle: string;
}

type SpecsRectangleProps = HTMLAttributes<HTMLDivElement> & {
  boundaries: RectangleBoundaries[];
  scaleFactor: number;
  labelComponent: React.ReactNode;
};

export default function SpecsRectangle(props: SpecsRectangleProps) {
  const { scaleFactor, boundaries, labelComponent, style, ...restProps } = props;

  const largestBoundary = boundaries.reduce((largest, boundary) => {
    if (
      largest === undefined ||
      boundary.height * boundary.width > largest.height * largest.width
    ) {
      return boundary;
    }

    return largest;
  });

  // set the relative sizes of the boundaries (largest boundary takes up 100% of the parent)
  // then sort boundaries from largest area to smallest area
  // the sorting is used for the layering so that the larger rectangles are on the bottom
  const processedBoundaries = boundaries
    .map(boundary => ({
      ...boundary,
      area: boundary.height * boundary.width,
      height: `${(100 * boundary.height) / largestBoundary.height}%`,
      width: `${(100 * boundary.width) / largestBoundary.width}%`,
      offsetX: `${(100 * boundary.offsetX) / largestBoundary.width}%`,
      offsetY: `${(100 * boundary.offsetY) / largestBoundary.height}%`,
    }))
    .sort((a, b) => b.area - a.area); // sort by area desc

  if (processedBoundaries.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        height: largestBoundary.height * scaleFactor,
        width: largestBoundary.width * scaleFactor,
      }}
    >
      <div
        style={{
          // create a fixed-aspect-ratio container
          // ref: https://css-tricks.com/aspect-ratio-boxes/
          paddingTop: `${(100 * largestBoundary.height) / largestBoundary.width}%`,
          height: 0,
          width: '100%',

          position: 'relative',
        }}
      >
        <div
          style={{
            // position content in the center of the fixed-aspect-ratio container
            // we use the top/left/transform technique rather than a flex parent because this element needs to have position: absolute
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            maxHeight: 'inherit',
            maxWidth: 'inherit',
            borderRadius: largestBoundary.cornerRadius * scaleFactor,
            ...style,
          }}
          {...restProps}
        >
          {processedBoundaries.map((boundary, index) => (
            <div
              // assume that a boundary can be uniquely identified by it's area (height*width) and it's corner-radius
              key={`${boundary.area}-${boundary.cornerRadius}`}
              style={{
                position: 'absolute',
                boxSizing: 'border-box',
                zIndex: index, // assumes boundaries have been sorted by size (largest -> smallest)
                height: boundary.height,
                width: boundary.width,
                top: boundary.offsetY,
                left: boundary.offsetX,
                borderRadius: boundary.cornerRadius * scaleFactor,
                borderColor: boundary.borderColor,
                borderStyle: boundary.borderStyle,
                borderWidth: boundary.borderWidth,
                background: boundary.backgroundStyle,
              }}
            />
          ))}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: boundaries.length + 1, // place this on top of the boundaries layers
            }}
          >
            {labelComponent}
          </div>
        </div>
      </div>
    </div>
  );
}
